import { useState } from "react";
import { RefreshCw, Users, CalendarCheck, Settings2, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useApi } from "@/hooks/useApi";
import { usePerm } from "@/lib/perms";
import { api } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { invalidate } from "@/lib/invalidate";
import { cn } from "@/lib/utils";

type SyncStatus = {
  ghlTokenConfigured?: boolean;
  last_sync_summary?: string;
  last_calendar_sync_summary?: string;
};

// Always-available sync menu in the app header: pull accounts (GHL
// opportunities) or the calendar without digging into Settings. When no
// GHL token is configured, the items route to Settings instead of erroring.
export function QuickSync() {
  const navigate = useNavigate();
  const { can } = usePerm();
  const { data: status, reload } = useApi<SyncStatus>("/api/sync/status");
  const [busy, setBusy] = useState<null | "accounts" | "calendar">(null);
  const configured = !!status?.ghlTokenConfigured;
  // Sync is admin work — staff roles don't get a button that only 403s.
  if (!can("sync.run.CREATE")) return null;

  const run = async (which: "accounts" | "calendar") => {
    if (!configured) { navigate("/settings?tab=data"); return; }
    setBusy(which);
    try {
      if (which === "accounts") {
        const r = await api.post<{ inserted: number; updated: number; skipped: number }>("/api/sync/ghl", {});
        toast.success("Accounts synced", { description: `${r.inserted} new, ${r.updated} updated, ${r.skipped} skipped` });
        invalidate("account", "settings");
      } else {
        const r = await api.post<{ inserted: number; updated: number; calendars: number }>("/api/sync/ghl/calendar", {});
        toast.success("Calendar synced", { description: `${r.inserted} new, ${r.updated} updated across ${r.calendars} calendar(s)` });
        invalidate("booking", "settings");
      }
      reload();
    } catch (e) {
      toast.error("Sync needs setup", { description: e instanceof Error ? e.message : "Check Settings → Data & Sync." });
    } finally {
      setBusy(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground"
          aria-label="Sync"
          title={configured ? "Sync from GoHighLevel" : "Connect GoHighLevel to sync"}
        >
          <RefreshCw className={cn("size-4", busy && "animate-spin")} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-60">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {configured ? "Sync from GoHighLevel" : "GoHighLevel not connected"}
        </DropdownMenuLabel>
        <DropdownMenuItem onClick={() => run("accounts")} disabled={busy != null}>
          <Users /> <span className="flex-1">Sync accounts</span>
          {busy === "accounts" && <RefreshCw className="size-3.5 animate-spin" />}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => run("calendar")} disabled={busy != null}>
          <CalendarCheck /> <span className="flex-1">Sync calendar</span>
          {busy === "calendar" && <RefreshCw className="size-3.5 animate-spin" />}
        </DropdownMenuItem>
        {(status?.last_sync_summary || status?.last_calendar_sync_summary) && <DropdownMenuSeparator />}
        {status?.last_sync_summary && (
          <div className="flex items-start gap-1.5 px-2 py-1 text-[11px] text-muted-foreground">
            <Check className="mt-0.5 size-3 shrink-0 text-primary" /> {status.last_sync_summary}
          </div>
        )}
        {status?.last_calendar_sync_summary && (
          <div className="flex items-start gap-1.5 px-2 py-1 text-[11px] text-muted-foreground">
            <Check className="mt-0.5 size-3 shrink-0 text-primary" /> {status.last_calendar_sync_summary}
          </div>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/settings?tab=data")}>
          <Settings2 /> Sync settings
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
